import type { Consent } from './types';

// Google Consent Mode v2 signal map. mvp_b_plan.md §4.1.1.
// security_storage is always granted (covers fraud-prevention/auth tokens).
type GcmSignal =
  | 'ad_storage'
  | 'ad_user_data'
  | 'ad_personalization'
  | 'analytics_storage'
  | 'personalization_storage'
  | 'functionality_storage'
  | 'security_storage';

type GcmState = Record<GcmSignal, 'granted' | 'denied'>;

interface DataLayerWindow extends Window {
  dataLayer?: unknown[];
}

function ensureGtag(): void {
  const w = window as DataLayerWindow & { gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== 'function') {
    // Mirror the standard gtag bootstrap so customers don't have to write it.
    w.gtag = function gtag() {
      (w.dataLayer as unknown[]).push(arguments as unknown as IArguments);
    };
  }
}

export function gcmStateFor(c: Consent | null): GcmState {
  const granted = (b: boolean) => (b ? 'granted' : 'denied') as 'granted' | 'denied';
  return {
    security_storage: 'granted',
    ad_storage: granted(!!c?.marketing),
    ad_user_data: granted(!!c?.marketing),
    ad_personalization: granted(!!c?.marketing),
    analytics_storage: granted(!!c?.analytics),
    personalization_storage: granted(!!c?.functional),
    functionality_storage: granted(!!c?.functional),
  };
}

// Step 1 of mvp_b_plan.md §5.2: synchronously, before anything else.
// Default everything to denied except security_storage; wait_for_update so
// Google tags pause briefly for a stored decision update on the next tick.
export function pushGcmDefault(): void {
  ensureGtag();
  const w = window as unknown as DataLayerWindow & {
    gtag: (...args: unknown[]) => void;
  };
  w.gtag('consent', 'default', { ...gcmStateFor(null), wait_for_update: 500 });
}

export function pushGcmUpdate(c: Consent): void {
  ensureGtag();
  const w = window as unknown as DataLayerWindow & {
    gtag: (...args: unknown[]) => void;
  };
  w.gtag('consent', 'update', gcmStateFor(c));
}

export function pushDataLayerEvent(c: Consent): void {
  ensureGtag();
  const w = window as DataLayerWindow;
  (w.dataLayer as unknown[]).push({
    event: 'cookiesie_consent_update',
    cookiesie_consent: c,
  });
}
